import { Space } from 'antd';
import AudioManager from 'components/AudioManager/AudioManager';
import DeviceControls from 'components/DeviceControls/DeviceControls';
import DeviceHistory from 'components/DeviceHistory/DeviceHistory';
import BaseModal from 'components/Modals/BaseModal';
import TextToggle from 'components/UI/TextToggle';
import VideoPlayer from 'components/VideoPlayer/VideoPlayer';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import DeviceMap from 'features/integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import { getLocationCoordinates } from 'features/integrations/features/ConnectedDevices/features/DeviceMap/utils/DeviceMapUtils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { sendDeviceCommand } from 'services/devices/commands/commands.service';
import { useKillswitchDeviceMutation } from 'services/devices/devices.api';

export default function DeviceSettings({ device, onSelectDevice }) {
  const [killswitchDevice, { isLoading: isKilling }] = useKillswitchDeviceMutation();
  
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      useRenderTracker('DeviceSettings');
    }
  } catch (e) {}
  const perms = usePerms();

  const [showVideo, setShowVideo] = useState(false);
  const [displayDevice, setDisplayDevice] = useState(device);
  const [initialized, setInitialized] = useState(false);
  const [displayMessage, setDisplayMessage] = useState();
  const [sending, setSending] = useState(false);
  const [audioManager, setAudioManager] = useState(false);
  const [audioOverride, setAudioOverride] = useState(null);

  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const computeMaintenanceState = (targetDevice) =>
    Boolean(targetDevice?.kill_switch) || targetDevice?.data?.[0]?.status === -1;

  const computeDisplayStatus = (targetDevice) => Number(targetDevice?.data?.[0]?.status ?? 0);

  const [maintenanceActive, setMaintenanceActive] = useState(
    computeMaintenanceState(displayDevice),
  );
  const previousStatusRef = useRef(
    computeDisplayStatus(displayDevice) === -1 ? 0 : computeDisplayStatus(displayDevice),
  );

  const createEditPermission = () => {
    const permres = perms.testPermission(
      [8, 9, 10, 11, 12],
      [
        `Create-Edit${device.system_type.replace(/\s/g, '')}${device.device_type
          .replace(/\s/g, '')
          .replace(/\//g, '-')}Connections`,
      ],
    );
    return permres;
  };

  useEffect(() => {
    //I think we only need to check initialized because react calls useEffects twice in debug/developer mode
    if (initialized) setDisplayDevice(device);
  }, [device]);

  useEffect(() => {
    if (!displayDevice) return;

    const currentStatus = computeDisplayStatus(displayDevice);
    if (currentStatus !== -1) previousStatusRef.current = currentStatus;
    setMaintenanceActive(computeMaintenanceState(displayDevice));
  }, [displayDevice]);

  const applyMaintenanceToDisplayState = (active) => {
    setDisplayDevice((prev) => {
      if (!prev) return prev;

      const updatedStatus = active ? -1 : (previousStatusRef.current ?? 0);
      const existingData = Array.isArray(prev.data) ? prev.data : [];
      const [firstEntry, ...restEntries] = existingData;

      return {
        ...prev,
        kill_switch: active ? 1 : 0,
        data: [
          {
            ...(firstEntry ?? {}),
            status: updatedStatus,
          },
          ...restEntries,
        ],
      };
    });
  };

  const onEnableMaintenanceMode = async () => {
    if (!perms.isMaintenanceOperator()) return;

    const optimisticState = !maintenanceActive;
    applyMaintenanceToDisplayState(optimisticState);
    setMaintenanceActive(optimisticState);

    try {
      await killswitchDevice({ id: displayDevice.id, state: optimisticState ? 1 : 0 }).unwrap();

      if (!optimisticState) {
        // Leaving maintenance; ensure previous status reflects current display
        const currentStatus = computeDisplayStatus(displayDevice);
        if (currentStatus !== -1) previousStatusRef.current = currentStatus;
      }
    } catch (err) {
      console.error('Error sending device command:', err.message);
      setDisplayMessage({
        type: 'error',
        message: 'Failed to send command!',
      });

      // Revert optimistic update
      const revertState = !optimisticState;
      applyMaintenanceToDisplayState(revertState);
      setMaintenanceActive(revertState);
    }
  };

  const deviceControlListener = async (evt, data) => {
    setSuccessMsg('');
    setErrorMsg('');

    switch (evt) {
      case 'update':
        setDisplayDevice(data);
        setInitialized(true);
        return;
      case 'submit':
        setSending(true);
        try {
          const {
            id,
            node_id,
            system_type,
            device_type,
            status,
            maintenanceOverride,
            comments, //does not exist in data
            interval,
            volume,
            repeat,
            audio,
          } = data;

          await sendDeviceCommand(
            { id, node_id, system_type, device_type },
            status === -1 ? 1 : status,
            comments || 'No Comment Provided',
            maintenanceOverride || false,
            {
              interval: interval || 0,
              volume,
              repeat,
              audio,
              immediate: status === -1,
            },
          );
          setSuccessMsg('Command successfully sent');
        } catch (error) {
          setErrorMsg(error.message);
        }
        setSending(false);
        return;
      case 'cancel-audio':
        try {
          sendDeviceCommand(
            {
              id: device.id,
              node_id: device.node_id,
              system_type: device.system_type,
              device_type: device.device_type,
            },
            7,
            '',
            false,
          );
        } catch (err) {
          console.error('Error sending device command:', err.message);
          setDisplayMessage({
            type: 'error',
            message: 'Failed to send command!',
          });
        }
        return;
      case 'audio-manager':
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const onSetTempAudio = (fileName) => {
    setAudioOverride({
      id: -1,
      name: fileName,
      displayName: fileName,
      relay_id: -1,
      device_ids: null,
    });

    setAudioManager(false);
  };

  const deviceMapBounds = useMemo(() => {
    const coords = getLocationCoordinates(displayDevice?.location);
    if (!coords) return null;
    return [coords, coords];
  }, [displayDevice?.location]);

  return (
    <div className="d-flex flex-column min-h-75vh gap-2 overflow-y-auto overflow-x-hidden">
      {/* Top */}
      <div className="d-flex flex-column gap-3">
        <div className="d-flex flex-column">
          {!showVideo && (
            <div className="w-100 overflow-hidden rounded-3" style={{ height: '260px' }}>
              <DeviceMap
                mode="group"
                devicesOverride={displayDevice ? [displayDevice] : []}
                mapBoundsOverride={deviceMapBounds}
              />
            </div>
          )}

          {showVideo && device.device_type?.toLowerCase() === 'overwatch vms' && device.id && (
            <div className="d-flex flex-fill">
              <VideoPlayer
                src={`${process.env.PUBLIC_API_URL}/camera_v2/${device.id}/hls/index.m3u8`}
              />
            </div>
          )}

          {device.device_type?.toLowerCase() === 'overwatch vms' && (
            <Space className="mt-2">
              <TextToggle options={['Map', 'Video']} value={showVideo} setValue={setShowVideo} />
            </Space>
          )}
        </div>

        {perms.isMaintenanceOperator() &&
          layout[device.device_type.toLowerCase()] &&
          device.device_type !== 'Manual Road Closure' && (
            <button
              className={`btn ${maintenanceActive ? 'btn-warning' : 'btn-primary'}`}
              onClick={onEnableMaintenanceMode}
            >
              {maintenanceActive ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
            </button>
          )}

        {createEditPermission() && layout[displayDevice.device_type.toLowerCase()] && (
          <DeviceControls
            device={displayDevice}
            displayMessage={displayMessage}
            on={deviceControlListener}
            sending={sending}
            audioOverride={audioOverride}
            successMsg={successMsg}
            errorMsg={errorMsg}
          />
        )}
      </div>

      {/* Bottom: device history panel */}
      {layout[device.device_type.toLowerCase()] && (
        <div className="d-flex flex-fill flex-basis-0">
          <DeviceHistory
            title={'Device History'}
            subTitle={device.alias}
            data={device?.data}
            deviceType={device.device_type}
          />
        </div>
      )}

      {/* Audio Manager */}
      <BaseModal
        contentClassName="overflow-auto"
        contentStyle={{ height: '75dvh' }}
        show={perms.testPermission([], ['ViewSirenAudioFiles']) && device?.camera && audioManager}
        onHide={() => setAudioManager(false)}
        closeButton
        size="lg"
        centered
      >
        <AudioManager
          deviceIds={[device?.id]}
          onClose={() => setAudioManager(false)}
          onPlayTemp={onSetTempAudio}
        />
      </BaseModal>
    </div>
  );
}
